/**
 * Groovy Personalization Preview — generic, config-driven renderer
 * ----------------------------------------------------------------
 * ONE shared renderer for every brand/product. It reads a per-product config
 * from an inline JSON block (<script type="application/json" id="gpp-config">)
 * that the `groovy-personalization-preview.liquid` snippet emits.
 *
 * The config is the SAME "design recipe" that will later live in a product
 * metafield (custom.personalization) and feed the laser-engraving file
 * generator — so what the customer previews == what gets engraved.
 *
 * Behaviour: when the shopper types their initials, a <canvas> overlay draws
 * the clean-plate product image + their letters at the calibrated fractional
 * positions, fading in over the gallery. Clearing the field hides it.
 *
 * galleryMode:
 *   "overlay"     — paint over the current gallery view, no navigation (pilot default,
 *                   needs no blank product slide; this is the "no blank slide" version).
 *   "blank-slide" — navigate the Flickity gallery to its last slide (a real blank-bag
 *                   product image) and paint there (BatterBox approach).
 */
(function () {
  'use strict';

  /* ── Load config ─────────────────────────────────────────────────────── */
  var cfgEl = document.getElementById('gpp-config');
  if (!cfgEl) return;
  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!CFG || !CFG.cleanPlate || !CFG.zones || !CFG.zones.length) return;

  /* Optional handle guard — only run on the intended product */
  if (CFG.handle && window.location.pathname.indexOf(CFG.handle) === -1) return;

  var MODE = CFG.mode || 'monogram';   // 'monogram' (per-letter) | 'fields' (full-word text zones)
  var GALLERY_MODE = CFG.galleryMode || 'overlay';
  var FONT  = CFG.font  || '"Courier New", Courier, monospace';
  var COLOR = CFG.color || '#e4e2dd';
  var ZONES = CFG.zones;               // monogram: [{role,cx,cy,size}] · fields: [{prop,cx,cy,size,font,weight,type,maxw,lineHeight,upper}]
  var SIZE_TUNE = 0.70;                // matches BatterBox calibration scaling
  var fieldInputs = [];                // fields mode: one input per zone, resolved in init

  /* ── State ───────────────────────────────────────────────────────────── */
  var inputEl = null, flkty = null, lastIdx = 0;
  var overlay = null, canvas = null, ctx = null;
  var showing = false, didNav = false;

  var bagImg = new Image(), bagLoaded = false;
  bagImg.crossOrigin = 'anonymous';
  bagImg.onload  = function () { bagLoaded = true; repaint(); };
  bagImg.onerror = function () {                       // retry without crossOrigin
    var fb = new Image();
    fb.onload = function () { bagImg = fb; bagLoaded = true; repaint(); };
    fb.src = CFG.cleanPlate;
  };
  bagImg.src = CFG.cleanPlate;

  /* ── Overlay covering the gallery viewport ───────────────────────────── */
  function buildOverlay(gallery) {
    if (overlay) return;
    if (getComputedStyle(gallery).position === 'static') gallery.style.position = 'relative';
    overlay = document.createElement('div');
    overlay.id = 'gpp-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = [
      'position:absolute', 'top:0', 'left:0', 'right:0', 'bottom:0',
      'z-index:20', 'opacity:0', 'transition:opacity .22s ease',
      'pointer-events:none', 'overflow:hidden',
    ].join(';');
    canvas = document.createElement('canvas');
    canvas.style.cssText = 'display:block;width:100%;height:100%;';
    overlay.appendChild(canvas);
    gallery.appendChild(overlay);
    ctx = canvas.getContext('2d');
  }

  /* ── Flickity helpers ────────────────────────────────────────────────── */
  function resolveFlickity(root) {
    if (!window.Flickity) return null;
    var cands = [root].concat(
      Array.prototype.slice.call(root.querySelectorAll('[data-flickity],.flickity-enabled'))
    );
    for (var i = 0; i < cands.length; i++) {
      var fl = window.Flickity.data(cands[i]);
      if (fl) return fl;
    }
    return null;
  }
  function goToLast() {
    if (GALLERY_MODE !== 'blank-slide' || didNav || !flkty) return;
    didNav = true;
    flkty.select(lastIdx, false, false);
  }

  function setVisible(v) { if (overlay) { overlay.style.opacity = v ? '1' : '0'; showing = v; } }

  /* ── Shared canvas sizing + clean-plate draw ─────────────────────────── */
  /* Register the clean plate to the EXACT rect of the gallery's product image
     (which may be top-pinned / letterboxed / object-fit:fill), NOT a naive
     contain-fit of the canvas — otherwise the overlay is offset from the real
     product photo and the text lands a few px off. */
  function plateRect(clientRect, dpr, W, H) {
    var gallery = overlay && overlay.parentNode;
    if (gallery) {
      var imgs = [].slice.call(gallery.querySelectorAll('img')).filter(function (i) {
        return i.offsetParent !== null && i.getBoundingClientRect().width > 100;
      });
      imgs.sort(function (a, b) { return b.getBoundingClientRect().width - a.getBoundingClientRect().width; });
      var img = imgs[0];
      if (img) {
        var ir = img.getBoundingClientRect();
        return { W: W, H: H,
          dX: (ir.left - clientRect.left) * dpr, dY: (ir.top - clientRect.top) * dpr,
          dW: ir.width * dpr, dH: ir.height * dpr };
      }
    }
    var iW = bagImg.naturalWidth || W, iH = bagImg.naturalHeight || H;   // fallback: contain-fit
    var sc = (iW / iH > W / H) ? H / iH : W / iW, dW = iW * sc, dH = iH * sc;
    return { W: W, H: H, dX: (W - dW) / 2, dY: (H - dH) / 2, dW: dW, dH: dH };
  }
  function fitPlate() {
    var rect = canvas.getBoundingClientRect(), dpr = window.devicePixelRatio || 1;
    var W = Math.round(rect.width * dpr) || 600, H = Math.round(rect.height * dpr) || 600;
    if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
    ctx.clearRect(0, 0, W, H);
    var d = plateRect(rect, dpr, W, H);
    ctx.drawImage(bagImg, d.dX, d.dY, d.dW, d.dH);
    return d;
  }

  /* ── Fields mode (full-word text zones, e.g. decanter Name/Age/Year/Message) ── */
  function propValue(prop) {
    var el = document.querySelector('[name="properties[' + prop + ']"]');
    return el ? String(el.value || '').trim() : '';
  }
  function wrapLines(text, maxPx) {
    var words = text.split(/\s+/), lines = [], cur = '';
    for (var i = 0; i < words.length; i++) {
      var t = cur ? cur + ' ' + words[i] : words[i];
      if (ctx.measureText(t).width > maxPx && cur) { lines.push(cur); cur = words[i]; }
      else cur = t;
    }
    if (cur) lines.push(cur);
    return lines;
  }
  function repaintFields() {
    if (!canvas || !ctx || !bagLoaded) return;
    /* Hide if nothing entered yet */
    var anyVal = false;
    for (var i = 0; i < ZONES.length; i++) { if (propValue(ZONES[i].prop)) { anyVal = true; break; } }
    if (!anyVal) { ctx.clearRect(0, 0, canvas.width || 1, canvas.height || 1); setVisible(false); didNav = false; return; }
    goToLast();
    if (GALLERY_MODE === 'blank-slide' && flkty && flkty.selectedIndex !== lastIdx) { setVisible(false); return; }

    var f = fitPlate();
    for (var z, val, k = 0; k < ZONES.length; k++) {
      z = ZONES[k];
      val = propValue(z.prop);
      if (!val) continue;
      if (z.upper !== false) val = val.toUpperCase();
      var fs = f.dH * z.size;
      ctx.save();
      ctx.font = (z.weight || 600) + ' ' + fs + 'px "' + (z.font || 'Georgia') + '", serif';
      ctx.fillStyle = z.color || COLOR;
      ctx.globalAlpha = z.alpha != null ? z.alpha : 0.93;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.28)'; ctx.shadowBlur = Math.max(1, fs * 0.05);
      var x = f.dX + z.cx * f.dW, y = f.dY + z.cy * f.dH;
      if (z.type === 'multiline') {
        var lines = wrapLines(val, (z.maxw || 0.2) * f.dW), lh = fs * (z.lineHeight || 1.4);
        var startY = y - (lines.length - 1) * lh / 2;
        for (var li = 0; li < lines.length; li++) ctx.fillText(lines[li], x, startY + li * lh);
      } else {
        /* Auto-shrink to fit within fitWidth (keeps long names/numbers inside the label) */
        if (z.fitWidth) {
          var tw = ctx.measureText(val).width, maxpx = z.fitWidth * f.dW;
          if (tw > maxpx) { fs = fs * (maxpx / tw); ctx.font = (z.weight || 600) + ' ' + fs + 'px "' + (z.font || 'Georgia') + '", serif'; }
        }
        ctx.fillText(val, x, y);
      }
      ctx.restore();
    }
    setVisible(true);
  }

  /* ── Render ──────────────────────────────────────────────────────────── */
  function repaint() {
    if (MODE === 'fields') return repaintFields();
    if (!canvas || !ctx || !bagLoaded) return;

    var raw = inputEl ? inputEl.value.trim().toUpperCase().replace(/[^A-Z]/g, '') : '';
    if (!raw) { ctx.clearRect(0, 0, canvas.width || 1, canvas.height || 1); setVisible(false); didNav = false; return; }

    goToLast();
    if (GALLERY_MODE === 'blank-slide' && flkty && flkty.selectedIndex !== lastIdx) { setVisible(false); return; }

    var rect = canvas.getBoundingClientRect(), dpr = window.devicePixelRatio || 1;
    var W = Math.round(rect.width * dpr) || 600, H = Math.round(rect.height * dpr) || 600;
    if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
    ctx.clearRect(0, 0, W, H);

    /* Contain-fit the clean bag */
    var iW = bagImg.naturalWidth || W, iH = bagImg.naturalHeight || H;
    var sc = (iW / iH > W / H) ? H / iH : W / iW;
    var dW = iW * sc, dH = iH * sc, dX = (W - dW) / 2, dY = (H - dH) / 2;
    ctx.drawImage(bagImg, dX, dY, dW, dH);

    /* Letters at calibrated fractions (size ref = shorter drawn dimension) */
    var ref = Math.min(dW, dH);
    for (var i = 0; i < ZONES.length; i++) {
      var z = ZONES[i];
      drawLetter(dX + z.cx * dW, dY + z.cy * dH, raw.charAt(i), ref * z.size * SIZE_TUNE);
    }
    setVisible(true);
  }

  function drawLetter(x, y, ch, fs) {
    if (!ch) return;
    ctx.save();
    ctx.font = fs + 'px ' + FONT;
    ctx.fillStyle = COLOR;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.10)';
    ctx.shadowBlur = Math.max(1, fs * 0.04);
    ctx.fillText(ch, x, y);
    ctx.restore();
  }

  /* ── Find the personalization input ──────────────────────────────────── */
  function findInput() {
    var names = [];
    if (CFG.input && CFG.input.property) names.push('input[name="properties[' + CFG.input.property + ']"]');
    names = names.concat([
      'input[name="properties[Initials]"]', 'input[name="properties[initials]"]',
      'input[name="properties[Monogram]"]', 'input[name="properties[Personalization]"]',
      'input[name="properties[Name]"]',
    ]);
    for (var i = 0; i < names.length; i++) { var el = document.querySelector(names[i]); if (el) return el; }
    var found = null;                                   // fallback: match by label text
    document.querySelectorAll('label').forEach(function (lbl) {
      if (found) return;
      var t = lbl.textContent.toLowerCase();
      if (t.indexOf('initial') > -1 || t.indexOf('monogram') > -1 || t.indexOf('letter') > -1) {
        found = lbl.control || document.getElementById(lbl.htmlFor);
      }
    });
    return found;
  }

  function addHint(anchorInput) {
    if (document.getElementById('gpp-hint')) return;
    var hint = document.createElement('p');
    hint.id = 'gpp-hint';
    hint.textContent = '★ Preview personalization above';
    var anchor = anchorInput.closest('.bold_option, .product-form__input, .field, [class*="option"]') || anchorInput.parentNode;
    anchor.parentNode.insertBefore(hint, anchor.nextSibling);
  }

  /* ── Init ────────────────────────────────────────────────────────────── */
  function init() {
    var gallery = document.querySelector('[data-product-gallery]')
      || document.querySelector('.product_gallery')
      || document.querySelector('.product-gallery')
      || document.querySelector('[class*="product"][class*="gallery"]');
    if (!gallery) return;
    buildOverlay(gallery);

    /* Inject Google Fonts used by the config (e.g. Cardo, Playfair Display) */
    if (CFG.googleFonts && !document.getElementById('gpp-fonts')) {
      var fl = document.createElement('link');
      fl.id = 'gpp-fonts'; fl.rel = 'stylesheet';
      fl.href = 'https://fonts.googleapis.com/css2?family=' + CFG.googleFonts + '&display=swap';
      document.head.appendChild(fl);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (showing) repaint(); });
    }

    function anyFieldValue() {
      if (MODE === 'fields') { for (var i = 0; i < ZONES.length; i++) if (propValue(ZONES[i].prop)) return true; return false; }
      return inputEl && inputEl.value.trim().toUpperCase().replace(/[^A-Z]/g, '');
    }

    var fTries = 0;
    var fPoll = setInterval(function () {
      flkty = resolveFlickity(gallery);
      if (flkty) {
        clearInterval(fPoll);
        lastIdx = flkty.slides.length - 1;
        flkty.on('change', function (idx) {          // swiping the gallery dismisses the overlay
          if (!anyFieldValue()) return;
          if (GALLERY_MODE === 'blank-slide') { (idx === lastIdx) ? repaint() : setVisible(false); }
          else setVisible(false);
        });
      } else if (++fTries > 40) clearInterval(fPoll);
    }, 100);

    var hintStyle = document.createElement('style');
    hintStyle.textContent = '#gpp-hint{display:none}@media(max-width:767px){#gpp-hint{display:block;font-size:11px;color:#888;margin-top:5px;letter-spacing:.01em}}';
    document.head.appendChild(hintStyle);

    var iTries = 0;
    var iPoll = setInterval(function () {
      if (MODE === 'fields') {
        /* Resolve one input per zone prop; ready when at least one exists */
        fieldInputs = [];
        for (var zi = 0; zi < ZONES.length; zi++) {
          var fe = document.querySelector('[name="properties[' + ZONES[zi].prop + ']"]');
          if (fe) fieldInputs.push(fe);
        }
        if (fieldInputs.length) {
          clearInterval(iPoll);
          fieldInputs.forEach(function (fe) {
            fe.addEventListener('input', repaint);
            fe.addEventListener('change', repaint);
          });
          inputEl = fieldInputs[0];
          setTimeout(repaint, 150);
          addHint(inputEl);
        } else if (++iTries > 80) clearInterval(iPoll);
        return;
      }
      inputEl = findInput();
      if (inputEl) {
        clearInterval(iPoll);
        inputEl.addEventListener('input', repaint);
        inputEl.addEventListener('change', repaint);
        setTimeout(repaint, 150);
        addHint(inputEl);
      } else if (++iTries > 80) clearInterval(iPoll);
    }, 100);

    window.addEventListener('resize', function () { if (showing) repaint(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 400); });
  } else {
    setTimeout(init, 400);
  }
})();
